<template>
  <div>
    <!--
      PR B4 - Pricing & Checkout master toggle.

      "Show prices to shoppers" lives at the TOP of this section, above
      Payments & Pricing and Store Limits & Security. The toggle binds to
      the existing `campaign.storefront_pricing` boolean and drives the
      parent's `storefrontPricingEnabled` state, which gates the
      price-dependent cards (Shipping, Fundraising, Coupons, Base margin)
      via the DependentDisclosure pattern below.

      The legacy in-line "Display Sales Price on Storefront" row inside
      CampaignPaymentOptions is hidden (kept in the component but
      v-if=false) so the data path (campaignStorefrontPricingEnabled +
      watcher + getData) stays untouched per the B4 scope guard
      "DO NOT change autosave logic, save chain, emit chain, or v-model
      bindings". The child's `setStorefrontPricing(val)` method is the
      bridge that lets the parent master toggle keep the child's internal
      state in sync without inverting the save direction.
    -->
    <editor-card
      title="Show prices to shoppers"
      description="Master switch for storefront pricing. Off = hide all pricing UI from shoppers."
      class="feature-card pricing-master-card"
      data-testid="pricing-master-card"
    >
      <div
        class="pricing-master-row"
        data-testid="pricing-master-row"
        @click="onMasterRowClick"
      >
        <div class="pricing-master-label">
          <div class="pricing-master-label-title" data-testid="pricing-master-label">
            Show prices to shoppers
          </div>
          <div class="pricing-master-label-help" data-testid="pricing-master-help">
            When on, shoppers see product prices and can pay.
          </div>
        </div>
        <v-switch
          :model-value="storefrontPricingEnabled"
          @update:modelValue="onMasterPricingToggle"
          data-testid="pricing-master-toggle"
          :disabled="disableForCampaignLifecycle || $store.getters.getIsNewOrderAdded"
          hide-details
          density="compact"
          @click.stop
        />
      </div>
    </editor-card>

    <v-form ref="form" v-model="primaryFormsValid">
      <!-- Payments & Pricing Options. Header removed; CampaignPaymentOptions
           renders its own (the legacy CampaignFeatures wrapped its children
           with a duplicate label). -->
      <editor-card title="Payments & Pricing" class="feature-card">
        <campaign-payment-options
          ref="paymentOptions"
          :campaign="campaign"
          :edit-mode="editMode"
          :disable-fields="disableForCampaignLifecycle"
          :disable-payment-method="disableForCampaignLifecycle"
          :campaign-price-limit-enabled="priceLimitEnabled"
          :coupons-enabled="couponsEnabled"
          :hide-storefront-pricing-row="true"
          :allowance-active="allowanceActive"
          @campaign-changed="onCampaignChanged"
          @update:storefront-pricing-enabled="onStorefrontPricingChanged"
          @update:company-allowance-enabled="onCompanyAllowanceEnabledChanged"
          @update:consolidate-tax-fee-enabled="onConsolidateTaxFeeChanged"
        />
        <!--
          Price-dependent cards. The "Show prices to shoppers" master toggle
          (storefront_pricing) lives at the TOP of this section (B4) and
          drives `storefrontPricingEnabled`. When pricing is OFF these cards
          collapse to a single inert "Off - prices hidden" row (children not
          rendered) via DependentDisclosure -- replacing the legacy
          dim-and-disable cascade. Collapsed children have no $ref, so their
          getData()/loadFromCampaign() are skipped by the existing ?.-guarded
          calls and they contribute nothing to the save payload (desired).
        -->
        <!--
          Shipping: the "Shipping cost" enable switch lives in
          StorePaymentOptions (restored 2026-10-22; PR #1982 had removed it).
          The legacy flat-rate / percentage sub-options (shipping_cost_type /
          shipping_cost_info) are NOT editable in this editor.
        -->
        <!--
          Fundraiser / Donations is hidden platform-wide unless an SA turns
          on the "Fundraising" switch (#623). Not rendered, so there is no
          $refs.fundraising and the save payload carries no fundraising key
          (the ?.-guarded calls below skip it): a store's saved fundraising
          settings are kept untouched.
        -->
        <dependent-disclosure
          v-if="enableFundraising"
          :parent="storefrontPricingEnabled"
          parent-label="Fundraising"
          inert-text="Off - prices hidden"
        >
          <campaign-fundraising
            ref="fundraising"
            :campaign="campaign"
            :edit-mode="editMode"
            :disable-fields="disableForCampaignLifecycle"
            :storefront-pricing-enabled="storefrontPricingEnabled"
            @campaign-changed="onCampaignChanged"
          />
        </dependent-disclosure>
        <dependent-disclosure
          :parent="storefrontPricingEnabled"
          parent-label="Coupons"
          inert-text="Off - prices hidden"
        >
          <campaign-coupons
            ref="coupons"
            :campaign="campaign"
            :edit-mode="editMode"
            :disable-fields="disableForCampaignLifecycle"
            :storefront-pricing-enabled="storefrontPricingEnabled"
            :consolidate-tax-fee-enabled="consolidateTaxFeeEnabled"
            @campaign-changed="onCampaignChanged"
            @update:coupons-enabled="onCouponsEnabledChanged"
            @consolidate-conflict="onConsolidateConflict"
          />
        </dependent-disclosure>
        <!--
          Per-store base-margin override. Price-dependent: collapses
          under the storefront_pricing master switch, same as
          shipping / fundraising / coupons. NULL inherits from the
          distributor default; if that is also NULL, a hardcoded 0.40
          fallback applies. Anchor id="base-margin" lets the new
          /pricing-checkout URL deep-link directly to this panel.
        -->
        <dependent-disclosure
          :parent="storefrontPricingEnabled"
          parent-label="Base margin"
          inert-text="Off - prices hidden"
        >
          <store-base-margin
            ref="baseMargin"
            :campaign="campaign"
            :edit-mode="editMode"
            :disable-fields="disableForCampaignLifecycle"
            :storefront-pricing-enabled="storefrontPricingEnabled"
            @campaign-changed="onCampaignChanged"
          />
        </dependent-disclosure>
      </editor-card>

      <!--
        PR-G (2026-06-22) Checkout IA refactor: the "Store Limits &
        Security" card was lifted out of StoreFeatures (Pricing section)
        into the new Checkout orchestrator (StoreCheckout.vue). The
        item-count limit and cart-$ limit are concepts the shopper
        experiences at checkout time, so they live on the Checkout page
        next to Identity / Custom Fields / Shipping / Shopper Allowance.

        The data flow is unchanged: the order-limits child writes
        ``items_count_limit`` and ``price_limit`` into the campaign on
        save. StoreFeatures no longer owns that child or its props.
      -->

      <!--
        Order Confirmation Options moved out of CampaignFeatures to its
        own "Notifications" sidebar section in the redesigned editor
        (Claude Design experiment). CampaignEmailConfig is mounted by
        CampaignAddEdit's #section-notifications slot. CampaignFeatures
        now owns only Payments & Pricing + Store Limits & Security.

        Store Message was removed from this section in PR B4. It moves
        to the Branding section in PR E3.
      -->
    </v-form>
  </div>
</template>

<script>
import CampaignPaymentOptions from "./StorePaymentOptions.vue";
import CampaignFundraising from "./StoreFundraising.vue";
import CampaignCoupons from "./StoreCoupons.vue";
// PR-G (2026-06-22): StoreOrderLimits import dropped here -- the card
// moved to the Checkout orchestrator (StoreCheckout.vue).
import StoreBaseMargin from "./StoreBaseMargin.vue";
import DependentDisclosure from "@/components/DependentDisclosure.vue";
import EditorCard from "./components/EditorCard.vue";
import { computeDisableFlags } from "./disableFlags.js";
import { useEnableFundraising } from "@/composables/useEnableFundraising";

export default {
  setup() {
    // Fundraiser / Donations is hidden platform-wide unless an SA turns it
    // on (#623).
    const { enableFundraising } = useEnableFundraising();
    return { enableFundraising };
  },

  components: {
    CampaignPaymentOptions,
    CampaignFundraising,
    CampaignCoupons,
    // CampaignOrderLimits removed in PR-G (moved to StoreCheckout).
    StoreBaseMargin,
    DependentDisclosure,
    EditorCard,
  },

  props: {
    campaign: Object,
    editMode: Boolean,
  },

  emits: ["campaign-changed", "update-tab-invalid"],

  data() {
    return {
      primaryFormsValid: true,
      _hasInitialized: false,

      // Cross-cutting state that flows between siblings via props.
      // Updated via events from children, passed down as props.
      storefrontPricingEnabled: false,
      // Allowance v1->v2 migration Slice 3 (2026-07-09): the v2 allowance
      // state, fetched from GET /api/stores/<id>/allowance-policy/ and passed
      // to StorePaymentOptions as ``:allowance-active``. It replaces the
      // write-orphaned ``company_allowance_personal_pay`` read that used to
      // seed the "Accept Shopper Payments" toggle. Mirrors the Slice-0
      // OrderLimits gate (StoreCheckout.allowanceActive).
      allowanceActive: false,
      consolidateTaxFeeEnabled: false,
      couponsEnabled: false,
      priceLimitEnabled: false,
    };
  },

  computed: {
    valid() {
      return this.primaryFormsValid !== false;
    },

    /**
     * The distributor's Stripe Connect account id, or "" when there is
     * none. Browser-side mirror of ``Distributor.charging_account_id``
     * (issue #2675): CANONICAL ``stripe_connect_account_id`` first,
     * LEGACY ``stripe_account_id`` as fallback, EMPTY STRING counts as
     * ABSENT on both (``handle_oauth_deauthorize`` clears the legacy
     * column by writing "", not NULL).
     *
     * Duplicated from StorePaymentOptions rather than shared, because it
     * is four lines and a shared helper module for it would be a heavier
     * dependency than the rule it carries. The two must agree; both are
     * pinned by tests against the same production column shapes.
     */
    distributorConnectAccountId() {
      const distributor = this.campaign?.distributor;
      if (!distributor) {
        return "";
      }
      return (
        distributor.stripe_connect_account_id ||
        distributor.stripe_account_id ||
        ""
      );
    },

    /**
     * Per-section disable flags for child components. Replaces the old
     * single `disableFields` boolean — see ./disableFlags.js for the
     * rationale. Reactive via the `campaign` prop and the Vuex getter.
     */
    disableFlags() {
      return computeDisableFlags({
        campaign: this.campaign,
        isNewOrderAdded: this.$store.getters.getIsNewOrderAdded,
      });
    },

    /**
     * Locks toggles only when the campaign is complete or active, or a
     * new order was just placed. Used by most sections — `has_orders`
     * is NOT a reason to lock these (the backend correctly applies
     * current rules to frozen historical amounts).
     */
    disableForActiveOrComplete() {
      return this.disableFlags.disableForActiveOrComplete;
    },

    /**
     * Adds `has_orders` on top of disableForActiveOrComplete. Retained
     * for any callers that still need the strictest historical gate;
     * the Shopper Experience holdouts (payment-method radio) no longer
     * use this since the Order Record Durability relaxation.
     */
    disableForOrders() {
      return this.disableFlags.disableForOrders;
    },

    /**
     * Locks ONLY when the campaign is complete or a new order was just
     * placed. Drops both `has_orders` and `active` from the gate. This
     * is the new minimum gate for all Shopper Experience sections
     * after the Order Record Durability relaxation, since the backend
     * now snapshots derived amounts at order time and applies current
     * rules to historical data correctly.
     */
    disableForCampaignLifecycle() {
      return this.disableFlags.disableForCampaignLifecycle;
    },
  },

  watch: {
    campaign: {
      immediate: true,
      handler(newVal) {
        if (this.editMode && !this._hasInitialized && newVal?._jv?.id) {
          this._hasInitialized = true;
          this.fillPreliminaryData();
        }
      },
    },

    // Allowance v1->v2 migration Slice 3 (2026-07-09): the watcher that
    // mirrored ``campaign.company_allowance_personal_pay`` into a local
    // ``companyAllowanceEnabled`` was removed. Its only consumer was the
    // ``<campaign-shipping-config> :company-allowance-enabled`` prop, and
    // shipping-config was removed from this editor (2026-07-08), leaving the
    // watcher dead. The "Accept Shopper Payments" toggle now derives from the
    // v2 allowance policy (``allowanceActive``), fetched below and passed to
    // StorePaymentOptions.

    valid(newVal) {
      this.$emit("update-tab-invalid", "features", !newVal);
    },
  },

  methods: {
    /**
     * Initializes cross-cutting state from campaign data and delegates
     * detailed loading to each child component.
     */
    fillPreliminaryData() {
      // Disable flags are now reactive `computed` properties on this
      // component (see disableForActiveOrComplete / disableForOrders),
      // so no manual recomputation is needed here.

      // Set cross-cutting state from campaign
      this.storefrontPricingEnabled =
        this.campaign.storefront_pricing || false;
      // Allowance v1->v2 migration Slice 3 (2026-07-09): the local
      // "Accept Shopper Payments" mirror (companyAllowanceEnabled) that read
      // ``company_allowance_personal_pay`` was removed. The v2 allowance
      // state is fetched by loadAllowanceState() and flows to
      // StorePaymentOptions via the ``allowanceActive`` prop.
      // Allowance v1->v2 (Slice 4): the legacy ``company_allowance`` store
      // column was dropped, and the Vuex ``companyAllowance`` state it fed had
      // no readers -- both are removed. Shopper allowance is a v2
      // allowance-policy concept (``allowanceActive``), fetched by
      // loadAllowanceState().
      this.consolidateTaxFeeEnabled =
        this.campaign.consolidate_tax_fee || false;
      this.couponsEnabled = this.campaign.coupons_enabled || false;
      this.priceLimitEnabled = !!this.campaign.price_limit;

      // Sync Vuex store with initial state
      this.$store.commit(
        "setStorefrontPricing",
        this.storefrontPricingEnabled
      );
      this.$store.commit(
        "setCollectSalesTaxEnabled",
        this.campaign.collect_sales_tax || false
      );
      this.$store.commit(
        "setCollectProcessingFeeEnabled",
        this.campaign.collect_processing_fee || false
      );
      this.$store.commit(
        "setConsolidateTaxFeeEnabled",
        this.consolidateTaxFeeEnabled
      );
      if (this.campaign.email_shopper) {
        this.$store.commit("setCompanyEmailEnabled", true);
      }

      // Slice 3: fetch the v2 allowance state so the "Accept Shopper
      // Payments" toggle in StorePaymentOptions derives from
      // allowance_policies (allowanceActive), not the retired
      // company_allowance_personal_pay column. Fire-and-forget; a missing /
      // inactive policy leaves the toggle off.
      this.loadAllowanceState();

      // Delegate to children after DOM update
      this.$nextTick(() => {
        this.$refs.paymentOptions?.loadFromCampaign(this.campaign);
        this.$refs.fundraising?.loadFromCampaign(this.campaign);
        this.$refs.coupons?.loadFromCampaign(this.campaign);
        // PR-G (2026-06-22): the orderLimits ref moved to StoreCheckout,
        // which calls its own loadFromCampaign during the Checkout
        // section's load cycle. Nothing to do here.
        this.$refs.baseMargin?.loadFromCampaign(this.campaign);
        // accessControl load now happens inside StoreShopper (the new
        // store-login section owner of is_restrict_email_domain /
        // email_domains). emailConfig load happens inside CampaignAddEdit's
        // notifications section ref. Neither is a child here anymore.
        //
        // storeMessage load removed in PR B4 (Store Message moves to
        // Branding in PR E3).
      });
    },

    /**
     * Called when the tab is activated. Sets payment method list based
     * on Stripe status, fetches used coupon codes, and refreshes
     * disabled state.
     */
    async loadComponent() {
      // Determine available payment methods based on Stripe account.
      // Reads the RESOLVED account (canonical column first, legacy as
      // fallback -- browser-side mirror of
      // ``Distributor.charging_account_id``, issue #2675), never the
      // legacy column alone. This runs on every activation of the
      // Pricing & Checkout tab and OVERWRITES the child's own list via
      // setPaymentMethodList, so keying it off the legacy column would
      // re-drop "Individual CC Payment" for a connected distributor the
      // moment the tab is opened -- the 2026-09-08 production bug.
      if (this.distributorConnectAccountId) {
        this.$refs.paymentOptions?.setPaymentMethodList([
          "Individual CC Payment",
          "Invoice / Payroll Deductions",
        ]);
      } else {
        this.$refs.paymentOptions?.setPaymentMethodList([
          "Invoice / Payroll Deductions",
        ]);
      }

      // Do NOT call this.updateCampaign(this.campaign) here. On
      // client-side navigation INTO Pricing & Checkout, loadComponent
      // runs in a $nextTick scheduled by StoreAddEdit.onActiveSectionChanged
      // BEFORE this section's fillPreliminaryData $nextTick fires
      // children's loadFromCampaign. Calling updateCampaign at this
      // point reads child getData() (which returns initial-data
      // defaults like storefront_pricing:false) and Object.assigns them
      // onto the campaign prop, CLOBBERING the persisted values.
      // Autosave runs updateCampaign on every save iteration; no caller
      // depends on it being run from loadComponent. See spec
      // store-features-load-component-no-clobber.spec.js for the
      // regression guard.

      // Fetch the coupon codes already used in orders, per code
      if (this.campaign && this.campaign._jv && this.campaign._jv.id) {
        try {
          let result = await this.$axios.get(
            `/campaign/getUsedCouponSummary/${this.campaign._jv.id}`
          );
          this.$refs.coupons?.loadUsedCouponSummary(result.data);
        } catch (err) {
          this.$store.dispatch('raiseError', err);
        }
      }

      // Disable flags are reactive `computed` (see
      // disableForActiveOrComplete / disableForOrders) — no manual
      // refresh needed.
    },

    /**
     * Assembles the final campaign payload by collecting data from all
     * child components. Called by CampaignAddEdit before save.
     *
     * IMPORTANT: The returned object shape must match what the backend
     * expects. Each child's getData() returns its slice of fields.
     */
    updateCampaign(campaign) {
      campaign.error = null;
      this.$emit("update-tab-invalid", "features", !this.valid);

      if (!this.valid) {
        campaign.error = "Shopper Experience Tab Incomplete";
      }

      // Collect data from all children
      // (CampaignEmailConfig was extracted into its own Notifications
      // section in the redesigned editor; its updateCampaign is now
      // invoked separately by CampaignAddEdit's save loop.)
      //
      // storeMessage removed from this section in PR B4 (moves to
      // Branding in PR E3).
      const payment = this.$refs.paymentOptions?.getData() || {};
      const fundraising = this.$refs.fundraising?.getData() || {};
      const coupons = this.$refs.coupons?.getData() || {};
      // PR-G (2026-06-22): orderLimits getData() is no longer collected
      // here -- the card moved to the StoreCheckout orchestrator, which
      // merges items_count_limit / price_limit on save as part of the
      // Checkout section's updateCampaign call.
      // Base-margin override slice (price-dependent; the ?.-guarded
      // getData() returns {} when the disclosure is collapsed and the
      // ref is absent, so nothing is merged onto the payload in that
      // case).
      const baseMargin = this.$refs.baseMargin?.getData() || {};
      // Access Control moved to the Store Login section (single-owner).
      // StoreFeatures no longer assembles is_restrict_email_domain /
      // email_domains -- StoreShopper.updateCampaign() owns them.

      // Merge all child data into the campaign object. Shipping removed
      // (back-office pass-through) -- no shipping data assembled here.
      Object.assign(
        campaign,
        payment,
        fundraising,
        coupons,
        baseMargin
      );

      return campaign;
    },

    /** A save was refused: the payment card shows the saved settings. */
    adoptSavedAccess(saved) {
      this.$refs.paymentOptions?.adoptSavedAccess?.(saved);
    },

    /**
     * Triggers validation on the primary form.
     * Returns true if the tab is INVALID (for the gate condition).
     */
    validateTab() {
      this.$refs.form?.validate();
      return !this.valid;
    },

    // --- Event handlers for cross-cutting state ---

    /**
     * PR B4 - master toggle handler. Mirrors the campaign field,
     * updates the parent state, propagates to the payment-options
     * child (so its internal `campaignStorefrontPricingEnabled` data
     * stays the source of truth for getData()), and routes through
     * the existing onStorefrontPricingChanged plumbing for the Vuex
     * store sync.
     */
    onMasterPricingToggle(val) {
      // Mirror the toggle onto the campaign prop -- the autosave loop
      // also reads the child's getData() which writes the same key,
      // so this is belt-and-suspenders, not the canonical write.
      if (this.campaign) {
        this.campaign.storefront_pricing = val;
      }
      // Keep the child's internal state in sync so its getData()
      // returns the new value on the next save iteration.
      this.$refs.paymentOptions?.setStorefrontPricing(val);
      this.onStorefrontPricingChanged(val);
      this.$emit("campaign-changed");
    },

    /**
     * Click-anywhere-on-row affordance. The v-switch has @click.stop so
     * clicks on the switch itself do not double-toggle.
     */
    onMasterRowClick() {
      if (this.disableForCampaignLifecycle || this.$store.getters.getIsNewOrderAdded) {
        return;
      }
      this.onMasterPricingToggle(!this.storefrontPricingEnabled);
    },

    onStorefrontPricingChanged(val) {
      this.storefrontPricingEnabled = val;
      this.$store.commit("setStorefrontPricing", val);
    },

    onCompanyAllowanceEnabledChanged() {
      // Slice 3: the local ``companyAllowanceEnabled`` mirror was removed
      // (the v2 ``allowanceActive`` prop is now the source).
      // Allowance v1->v2 (Slice 4): the Vuex ``setCompanyAllowance(0)`` sync
      // was removed with the dead ``companyAllowance`` Vuex state (no readers).
      // Handler retained as a no-op so the child ``update:company-allowance-
      // enabled`` emit still has a listener without a console warning.
    },

    /**
     * Slice 3 (2026-07-09): fetch the v2 allowance policy so the
     * "Accept Shopper Payments" toggle (in StorePaymentOptions) knows
     * whether a per-shopper allowance is configured. Self-contained read
     * (mirrors StoreAllowancePolicy.load / StoreCheckout.loadAllowanceState);
     * a missing / inactive policy leaves ``allowanceActive`` false.
     */
    async loadAllowanceState() {
      const storeId = this.campaign?._jv?.id;
      if (!storeId || !this.$axios) {
        this.allowanceActive = false;
        return;
      }
      try {
        const resp = await this.$axios.get(
          `/api/stores/${storeId}/allowance-policy/`
        );
        const policy = resp && resp.data && resp.data.policy;
        this.allowanceActive = !!(policy && policy.is_active !== false);
      } catch (e) {
        this.allowanceActive = false;
      }
    },

    // onCompanyAllowanceChanged removed in PR-G -- the Shopper
    // Allowance card moved to the Checkout orchestrator and owns the
    // numeric value there. The Vuex commit lives in the StoreCheckout
    // child's emit handler.

    onConsolidateTaxFeeChanged(val) {
      this.consolidateTaxFeeEnabled = val;
      this.$store.commit("setConsolidateTaxFeeEnabled", val);
    },

    onCouponsEnabledChanged(val) {
      this.couponsEnabled = val;
    },

    onPriceLimitEnabledChanged(val) {
      this.priceLimitEnabled = val;
    },

    onItemsCountLimitEnabledChanged(/* val */) {
      // Currently no cross-cutting state for items count limit,
      // but the event is captured for future use.
    },

    onConsolidateConflict() {
      this.$refs.paymentOptions?.handleConsolidateConflict();
    },

    onCampaignChanged() {
      this.$emit("campaign-changed");
    },
  },
};
</script>

<style scoped>
.radio-flat-rate {
  min-height: 70px;
  margin-bottom: 0px !important;
}
.blockalert {
  font-size: 18px;
  font-weight: bold;
  padding-left: 0;
  color: rgb(239, 4, 5);
}
.shopper-payment {
  display: flex;
}
.shopper-payment .shopper-warning {
  margin-top: 8px;
  margin-left: -10px;
}

.displayStorefrontFundraisingToggle {
  margin-top: 0px !important;
}

.radio-hint {
  font-size: 12px;
  opacity: 0.7;
  position: relative !important;
  bottom: 10px !important;
  left: 33px !important;
  max-width: 450px;
}

.text-label {
  font-size: 16px;
  opacity: 0.7;
}

.smart-shipping-help-text {
  font-size: 12px;
  opacity: 0.7;
}

.dotted {
  border: none;
  border-top: 2px dotted var(--tf-red);
  color: var(--tf-bg);
  background-color: var(--tf-bg);
}

.dotted-box {
  border: none;
  border-top: 2px dotted var(--tf-border-dotted);
  border-left: 2px dotted var(--tf-border-dotted);
  border-right: 2px dotted var(--tf-border-dotted);
  border-bottom: 2px dotted var(--tf-border-dotted);
}

.ship-from-address-field input {
  width: 500px !important;
}

.dropzone .dz-preview .dz-image {
  overflow: hidden !important;
  min-width: 250;
  max-width: 900px;
}
.dropzone {
  overflow: hidden !important;
  margin-right: 15px;
}

#dropzoneMailDivId {
  overflow: hidden !important;
  min-width: 250;
  max-width: 900px;
}

.brand-image {
  width: 100% !important;
  height: 100% !important;
  max-height: 150px !important;
  max-width: 800px !important;
  min-width: 200px !important;
}

.mt-8 {
  margin-top: 32px;
}

.ck-editor__main {
  border-left: 0px;
  border-right: 0px;
  border-bottom: 0px;
}

.text-right {
  text-align: right;
  margin-top: 20px;
}

.email-creator {
  width: 83% !important;
}

tr:hover {
  background: transparent !important;
}

.credit-card-tooltip {
  margin-top: -3px;
}

.feature-label {
  font-size: 18px !important;
}

.feature-content-left {
  padding-left: 16px !important;
}

.feature-content-right {
  padding-right: 16px !important;
}

/* Density pass (Jake, 2026-08-26): the Payments & Pricing section read
   as "a *ton* of whitespace". The two biggest contributors here were
   this card's own 14/28px outer margins and the gap the collapsible
   Fundraising / Coupons / Base-margin disclosures left between each
   other. Both step down onto the shared --tf-sp-* scale.

   Scoped, so this reaches only StoreFeatures' own cards. StorePaymentOptions
   declares its own scoped `.feature-card` and is unaffected; the shared
   EditorCard chrome (used by every other editor section) is untouched. */
.feature-card {
  border: 1px solid var(--tf-border) !important;
  margin-top: var(--tf-sp-2, 8px);
  margin-bottom: var(--tf-sp-4, 16px);
}

/* The disclosure rows stack directly inside the Payments & Pricing card.
   `:deep()` is required because DependentDisclosure is a separate
   component, and it is safe to use here precisely BECAUSE the block is
   scoped: the generated attribute selector confines the rule to
   disclosures rendered by this component. StoreBaseMargin -- the other
   DependentDisclosure consumer -- keeps its own spacing. */
.feature-card :deep(.dependent-disclosure) + :deep(.dependent-disclosure) {
  margin-top: var(--tf-sp-2, 8px);
}
.feature-card :deep(.dependent-disclosure__inert-row) {
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
}

/* PR B4 master toggle row */
.pricing-master-card {
  margin-top: 0 !important;
}
.pricing-master-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 0;
  gap: 16px;
}
.pricing-master-row .pricing-master-label {
  flex: 1;
  font-size: 14px;
  color: var(--tf-fg-2);
}

.short-flex {
  height: 50px;
}

.v-input {
  margin-bottom: 0px;
  margin-top: 0px;
}

.feature-content {
  margin-left: 16px;
}

.feature-drawer {
  /* Very subtle red-tinted off-white drawer background.
     No precise --tf-* match (closest is --tf-surface-muted #fafafa);
     route through var() with the original hex as fallback so the
     token can be filled in later without touching this file. */
  background: var(--tf-surface-muted-warm, #fcf9f9);
  margin-left: 8px;
  margin-right: 8px;
}

.feature-divider-flex {
  padding-bottom: 0px !important;
  padding-top: 0px !important;
}
</style>
